import React from 'react';
import { styled } from '@mui/material';

import { useViewport } from 'contexts/viewport';
import { Checkbox, Divider, MenuItem, Select, Stack, Text } from 'shell/components';
import {
    DEFAULT_HOUR_OPTION,
    DEFAULT_WEEKDAY_OPTION,
    HOURS_OPTIONS,
    TIME_ZONE_LIST,
    WEEKDAY_OPTIONS,
} from 'utils/constants';

const appendTimeZonePostFix = (option, timeZonePostFix) => {
    return timeZonePostFix ? { ...option, title: `${option.title} ${timeZonePostFix}` } : option;
};

const InstructorNotifications = ({ timeZone, preferences, onChange }) => {
    const { device } = useViewport();

    const instructorItems = [
        {
            key: 'project_overview',
            value: preferences?.project_overview?.email !== false,
            title: 'Project Overview',
            description:
                'Be informed on the average project completion and the number of teams marked as “On Track”, “Caution” or “Off Track”.',
        },
        {
            key: 'review_suggestions',
            value: preferences?.review_suggestions?.email !== false,
            title: 'Suggested to Review',
            description:
                'Receive suggestions on teams that could use extra attention, based on alerts brought up by their activities on the platform.',
        },
        {
            key: 'unread_messages',
            value: preferences?.unread_messages?.email !== false,
            title: 'Unread Messages',
            description: 'Be informed of any conversation that you have not viewed.',
        },
    ];

    const timeZonePostFix = TIME_ZONE_LIST.find((tz) => tz.text === timeZone)?.abbr || 'GMT';

    const shouldReceiveWeeklySummaries = preferences?.weekly_summaries?.email !== false;

    return (
        <>
            <CustomCheckbox
                checked={shouldReceiveWeeklySummaries}
                label={
                    <Stack sx={{ py: 1 }} spacing={2}>
                        <Text variant={'p1'}>Weekly Summaries</Text>
                        <Text color={'light'} variant={'p2'}>
                            Receive a summary every week with updates you prefer such as team progress, status updates,
                            and reminders for unread messages.
                        </Text>
                    </Stack>
                }
                onChange={(_, checked) => onChange('weekly_summaries', checked)}
            />
            <Stack direction={device === 'mobile' ? 'column' : 'row'} spacing={4}>
                <Select
                    disabled={!shouldReceiveWeeklySummaries}
                    label={'Receive Every'}
                    labelId={'profile-notifications-weekday'}
                    value={
                        WEEKDAY_OPTIONS.find(({ value }) => value === preferences?.weekly_summaries?.weekday)?.value ||
                        DEFAULT_WEEKDAY_OPTION.value
                    }
                    onChange={(e) => onChange('weekday', e.target.value)}
                >
                    {WEEKDAY_OPTIONS.map(({ title: value, value: key }) => (
                        <MenuItem key={key} value={key}>
                            {value}
                        </MenuItem>
                    ))}
                </Select>
                <Select
                    disabled={!shouldReceiveWeeklySummaries}
                    label={'Preferred Time'}
                    labelId={'profile-notifications-hour'}
                    value={
                        appendTimeZonePostFix(
                            HOURS_OPTIONS.find(({ value }) => value === preferences?.weekly_summaries?.time) ||
                                DEFAULT_HOUR_OPTION,
                            timeZonePostFix
                        ).value
                    }
                    onChange={(e) => onChange('time', e.target.value)}
                >
                    {HOURS_OPTIONS.map((option) => appendTimeZonePostFix(option, timeZonePostFix)).map(
                        ({ title: value, value: key }) => (
                            <MenuItem key={key} value={key}>
                                {value}
                            </MenuItem>
                        )
                    )}
                </Select>
            </Stack>
            <Divider />
            <Text color={!shouldReceiveWeeklySummaries ? 'disabled' : 'default'} variant={'sh2'} weight={'semibold'}>
                Include Notifications For
            </Text>
            {instructorItems.map(({ key, value, title, description }) => (
                <CustomCheckbox
                    key={key}
                    checked={value}
                    disabled={!shouldReceiveWeeklySummaries}
                    label={
                        <Stack sx={{ py: 1 }} spacing={2}>
                            <Text color={!shouldReceiveWeeklySummaries ? 'disabled' : 'default'} variant={'p1'}>
                                {title}
                            </Text>
                            <Text color={!shouldReceiveWeeklySummaries ? 'disabled' : 'light'} variant={'p2'}>
                                {description}
                            </Text>
                        </Stack>
                    }
                    onChange={(_, checked) => onChange(key, checked)}
                />
            ))}
            <CustomCheckbox
                checked={
                    preferences?.status_updates_ontrack?.email !== false ||
                    preferences?.status_updates_caution?.email !== false ||
                    preferences?.status_updates_offtrack?.email !== false
                }
                disabled={!shouldReceiveWeeklySummaries}
                label={
                    <Stack sx={{ py: 1 }} spacing={2}>
                        <Text color={!shouldReceiveWeeklySummaries ? 'disabled' : 'default'} variant={'p1'}>
                            Status Updates
                        </Text>
                        <Text color={!shouldReceiveWeeklySummaries ? 'disabled' : 'light'} variant={'p2'}>
                            Be informed of team status updates based on the indications you select.
                        </Text>
                    </Stack>
                }
                onChange={(_, checked) => onChange('status_updates', checked)}
            />
            <Stack sx={{ ml: 8 }} spacing={2}>
                <CustomCheckbox
                    checked={preferences?.status_updates_ontrack?.email !== false}
                    disabled={!shouldReceiveWeeklySummaries}
                    label={'“On Track” Teams'}
                    onChange={(_, checked) => onChange('status_updates_ontrack', checked)}
                />
                <CustomCheckbox
                    checked={preferences?.status_updates_caution?.email !== false}
                    disabled={!shouldReceiveWeeklySummaries}
                    label={'“Caution” Teams'}
                    onChange={(_, checked) => onChange('status_updates_caution', checked)}
                />
                <CustomCheckbox
                    checked={preferences?.status_updates_offtrack?.email !== false}
                    disabled={!shouldReceiveWeeklySummaries}
                    label={'“Off Track” Teams'}
                    onChange={(_, checked) => onChange('status_updates_offtrack', checked)}
                />
            </Stack>
        </>
    );
};

const CustomCheckbox = styled(Checkbox)(() => {
    return {
        '& > .MuiCheckbox-root': {
            alignSelf: 'flex-start',
        },
    };
});

export default InstructorNotifications;
